import { Injectable, inject } from "@angular/core";
import { Router } from "@angular/router";
import {
  getCurrentUser,
  fetchAuthSession,
  signOut,
  fetchUserAttributes,
  updatePassword,
} from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import { UsageOverviewDialogService } from "src/app/pages/billing/billing-status-widget/usage-overview-dialog.service";
import { LoaderService } from "src/app/shared/components/loader/loader.service";

@Injectable({
  providedIn: "root",
})
export class SessionService {
  private readonly loaderService = inject(LoaderService);
  private readonly usageOverviewDialog = inject(UsageOverviewDialogService);

  private oauthRedirectSettled: Promise<void> = Promise.resolve();
  private oauthRedirectFailed = false;

  constructor(private routes: Router) {}

  /**
   * Must run before Amplify.configure. When the app loads on the way back from
   * a social sign-in, Amplify settles the redirect asynchronously, and on
   * failure (user cancelled, provider error) it releases pending session
   * lookups before clearing its in-flight flag. A lookup landing in that gap
   * never resolves, which hangs the route guards. Hold session lookups until
   * Amplify reports the outcome instead.
   */
  trackOAuthRedirect(userPoolClientId: string): void {
    let isInflight = false;
    try {
      isInflight =
        localStorage.getItem(
          `CognitoIdentityServiceProvider.${userPoolClientId}.inflightOAuth`,
        ) === "true";
    } catch {}
    if (!isInflight) {
      return;
    }

    this.oauthRedirectSettled = new Promise<void>((resolve) => {
      const settle = () => {
        stopListening();
        clearTimeout(timeout);
        resolve();
      };
      const stopListening = Hub.listen("auth", ({ payload }) => {
        if (payload.event === "signInWithRedirect_failure") {
          this.oauthRedirectFailed = true;
          settle();
        } else if (payload.event === "signInWithRedirect") {
          settle();
        }
      });
      const timeout = setTimeout(settle, 15000);
    });
  }

  async isUserLoggedIn(): Promise<boolean> {
    const result = await this.getLoggedInUser();
    const isSignedIn =
      result?.session.tokens?.idToken?.toString() !== undefined;
    if (isSignedIn) {
      return true;
    } else {
      return false;
    }
  }

  async signOutUser(returnUrl?: string) {
    this.loaderService.show();
    try {
      this.usageOverviewDialog.resetOnSignOut();
      await signOut({ global: false });
      // After a failed social sign-in the current URL is the provider's error
      // callback, not somewhere worth returning to.
      const oauthRedirectFailed = this.oauthRedirectFailed;
      this.oauthRedirectFailed = false;
      const loginPath = oauthRedirectFailed
        ? "/auth/login?oauthError=true"
        : returnUrl
          ? `/auth/login?returnUrl=${encodeURIComponent(returnUrl)}`
          : "/auth/login";
      await this.routes.navigateByUrl(loginPath);
    } finally {
      this.loaderService.hide();
    }
  }

  async getLoggedInUser() {
    let sess = null;
    await this.oauthRedirectSettled;
    try {
      const session = await fetchAuthSession();
      const { username, userId, signInDetails } = await getCurrentUser();
      sess = {
        session: session,
        username: username,
        userId: userId,
        signInDetails: signInDetails,
      };
    } catch (ex) {}
    return sess;
  }
  async getToken() {
    const session = await fetchAuthSession();

    return session.tokens?.idToken?.toString();
  }

  async changePassword(
    oldPassword: string,
    newPassword: string,
  ): Promise<void> {
    await updatePassword({ oldPassword, newPassword });
  }
}
